import { flip, offset, shift } from '@floating-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useFloatingElement } from '@/hooks/use-floating-element';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

import { Card, CardBody, CardItemGroup } from '@/components/tiptap-editor/tiptap-ui-primitive/card';

import { getCodeMarkBoundingRect, getCodeMarkRange } from './get-code-mark-rect';

import './zycode-inline-bubble.scss';

const FLOATING_Z_INDEX = 1000;

export interface ZyCodeInlineBubbleProps {
    editor?: import('@tiptap/react').Editor | null;
}

export function ZyCodeInlineBubble({ editor: providedEditor }: ZyCodeInlineBubbleProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const [codeRect, setCodeRect] = useState<DOMRect | null>(null);
    const [codeRange, setCodeRange] = useState<{ from: number; to: number } | null>(null);
    const [dismissedByOutsideClick, setDismissedByOutsideClick] = useState(false);
    const bubbleRef = useRef<HTMLDivElement | null>(null);

    const updateCodeRect = useCallback(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }
        if (editor.isActive('code')) {
            setCodeRect(getCodeMarkBoundingRect(editor));
            setCodeRange(getCodeMarkRange(editor));
        } else {
            if (bubbleRef.current?.contains(document.activeElement)) {
                return;
            }
            setCodeRect(null);
            setCodeRange(null);
            setDismissedByOutsideClick(false);
        }
    }, [editor]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            setCodeRect(null); // eslint-disable-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-external-store-subscription -- TODO: Rewrite this so we don't need to disable this rule
            setCodeRange(null); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription -- TODO: Rewrite this so we don't need to disable this rule
            setDismissedByOutsideClick(false); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription -- TODO: Rewrite this so we don't need to disable this rule
            return;
        }

        updateCodeRect();
        editor.on('selectionUpdate', updateCodeRect);

        const handleScroll = () => {
            if (editor.isActive('code')) {
                setCodeRect(getCodeMarkBoundingRect(editor));
            }
        };

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            editor.off('selectionUpdate', updateCodeRect);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [editor, updateCodeRect]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }
        const editorRoot = editor.view.dom as HTMLElement;
        const onPointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (editorRoot.contains(target) || bubbleRef.current?.contains(target)) {
                setDismissedByOutsideClick(false);
                return;
            }
            setDismissedByOutsideClick(true);
        };
        document.addEventListener('pointerdown', onPointerDown, true);

        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [editor]);

    const attrs = editor?.isActive('code') ? editor.getAttributes('code') : {};
    const preserveWhitespace = !!attrs.preserveWhitespace;
    const allowWrap = !!attrs.allowWrap;

    const handleSetAttributes = useCallback(
        (nextPreserve: boolean, nextAllow: boolean) => {
            if (!editor || editor.isDestroyed) return;
            if (codeRange) {
                editor.chain().focus().setTextSelection(codeRange).run();
            }
            editor
                .chain()
                .focus()
                .extendMarkRange('code')
                .updateAttributes('code', { preserveWhitespace: nextPreserve, allowWrap: nextAllow })
                .run();
        },
        [editor, codeRange],
    );

    const show = editor?.isActive('code') === true && codeRect !== null && !dismissedByOutsideClick;

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show ?? false, codeRect, FLOATING_Z_INDEX, {
        placement: 'bottom-start',
        middleware: [
            offset(8),
            flip({
                mainAxis: true,
                crossAxis: false,
            }),
            shift(),
        ],
    });

    const setBubbleRef = useCallback(
        (node: HTMLDivElement | null) => {
            bubbleRef.current = node;
            ref(node);
        },
        [ref],
    );

    if (!isMounted || !show || !codeRect) {
        return null;
    }

    const bubbleContent = (
        <div
            ref={setBubbleRef}
            style={style}
            {...getFloatingProps()}
            className="tiptap-zycode-inline-bubble"
            role="dialog"
            aria-label="Inline code options"
        >
            <Card>
                <CardBody>
                    <CardItemGroup orientation="vertical">
                        <label className="tiptap-zycode-inline-bubble__checkbox-label">
                            <input
                                type="checkbox"
                                checked={preserveWhitespace}
                                onChange={e => handleSetAttributes(e.target.checked, allowWrap)}
                            />
                            Preserve spaces
                        </label>
                        <label className="tiptap-zycode-inline-bubble__checkbox-label">
                            <input
                                type="checkbox"
                                checked={allowWrap}
                                onChange={e => handleSetAttributes(preserveWhitespace, e.target.checked)}
                            />
                            Allow wrapping
                        </label>
                    </CardItemGroup>
                </CardBody>
            </Card>
        </div>
    );

    return createPortal(bubbleContent, document.body);
}
